import { pick, values, isBlank, flatten, isObjectLike } from 'gleam-shared/helpers/nodash'
import toSentence from 'gleam-shared/helpers/toSentence'

export const SERVER_ERROR_TITLE = 'Error occurred on the server'
export const CLIENT_ERROR_TITLE = 'Error occurred on the client'
export const UNKNOWN_ERROR_TITLE = 'Unexpected error occured'

export default function humanizeError(error) {
  let title = null
  let description = null

  if (error?.response) {
    const cause = retrieveCause(error)
    const { url, statusText, status } = error.response

    if (isBlank(cause) || isDocument(cause) || isDebug(cause)) {
      title = formatHttpTitle(status)
      description = `${formatHttpDescription(url, statusText, status)}. ${formatCallToAction({ url, statusText, status })}`
    } else if (isObjectLike(cause)) {
      title = SERVER_ERROR_TITLE
      description = flatten(values(cause)).map(toSentence).join(' ')
    } else {
      title = SERVER_ERROR_TITLE
      description = toSentence(cause)
    }
  } else if (error?.message) {
    title = CLIENT_ERROR_TITLE
    description = `${toSentence(error.message)} ${formatCallToAction(error)}`
  } else {
    title = UNKNOWN_ERROR_TITLE
    description = formatCallToAction(error)
  }

  return [title, description]
}

function retrieveCause(error) {
  return error.cause ?? error.response.data?.error ?? error.response.data?.errors ?? error.response.data
}

function formatHttpTitle(status) {
  if (status >= 500) {
    return SERVER_ERROR_TITLE
  } else {
    return CLIENT_ERROR_TITLE
  }
}

function formatHttpDescription(url, statusText, status) {
  const formattedStatus = formatHttpStatus(statusText, status)

  if (url && formattedStatus) {
    return `${url} replied with ${formattedStatus}`
  } else if (url) {
    return `${url} couldn't be requested`
  } else if (formattedStatus) {
    return formattedStatus
  } else {
    return 'A request couldn\'t be made'
  }
}

function formatHttpStatus(statusText, status) {
  if (statusText && status) {
    return `${statusText} (HTTP ${status})`
  } else if (status) {
    return `HTTP ${status}`
  }
}

function formatCallToAction(error) {
  const email = 'support@gleam.io'
  const subject = `Problem at ${window.location.href}`
  const body = `Hello, Gleam!\n\nI did: ...\nI expected: ...\nI got: ...\nI would like to mention: ...\nI've attached a screenshot!\n\nReferrer: ${document.referrer || 'None'}\nBrowser: ${navigator.userAgent}\nError: ${jsonError(error)}`
  const mailto = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  return `Please try again, or take a screenshot and contact support at [${email}](${mailto})`
}

function jsonError(error) {
  if (error?.response?.statusText) {
    return tryStringify(pick(error.response, 'data', 'url', 'statusText', 'status'))
  } else if (error?.message) {
    return error.message
  } else if (error) {
    return tryStringify(error)
  } else {
    return 'Nil'
  }
}

function tryStringify(error) {
  try {
    return JSON.stringify(error)
  } catch (jsonError) {
    console.warn('Could not stringify the error:', error, jsonError)
    return 'The occurred error could not be stringified!'
  }
}

function isDocument(error) {
  return /<html/.test(error)
}

function isDebug(error) {
  return /.rb, line|\(around line/.test(error)
}
